<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { useWizardForms } from '@/composables/useWizardForms'
import WizardFormDetailsForm from '@/components/organizer/wizard-forms/WizardFormDetailsForm.vue'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'

function isAxiosError(
  e: unknown,
): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

const { t } = useI18n()
const router = useRouter()
const { user } = useAuth()
const { createForm } = useWizardForms()

const loading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')

async function handleSubmit(payload: { name: string; description: string | null }): Promise<void> {
  errors.value = {}
  generalError.value = ''
  loading.value = true

  // user.value is guaranteed to be non-null here because of route guard (requiresAuth)
  const organisationId = user.value?.organisations[0]?.id

  if (!organisationId) {
    generalError.value = t('common.error_generic')
    loading.value = false
    return
  }

  try {
    const created = await createForm({
      organisation_id: organisationId,
      name: payload.name,
      description: payload.description,
    })
    void router.push(`/organizer/wizard-forms/${created.id}`)
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response.status === 422 && e.response.data.errors) {
      errors.value = e.response.data.errors
    } else {
      generalError.value = t('common.error_generic')
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div class="mb-6">
        <RouterLink
          to="/organizer/wizard-forms"
          class="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1"
        >

          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          {{ t('wizard_forms.form.back_to_list') }}
        </RouterLink>
      </div>

      <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
        <h1 class="text-2xl font-bold text-white mb-6">{{ t('wizard_forms.form.create_heading') }}</h1>

        <div
          v-if="generalError"
          class="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
        >
          {{ generalError }}
        </div>

        <WizardFormDetailsForm
          :loading="loading"
          :errors="errors"
          @submit="handleSubmit"
        />
      </div>
    </main>
  </OrganizerLayout>
</template>
